/**
 * Overlay.jsx — Browser Source para OBS / TikTok Studio
 * Tamaño recomendado: 680 × 300 (se adapta al contenido)
 * No necesita CSS adicional — el fondo se pone transparente automáticamente
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";

const RED = "#e31837";
const WHITE = "#ffffff";

function getBackendUrl() {
  const { protocol, hostname, port } = window.location;
  if (hostname === "localhost" && (port === "3000" || port === "3002"))
    return `${protocol}//${hostname}:3001`;
  return window.location.origin;
}

function TimerBar({ progress }) {
  return (
    <div style={{ height: 6, borderRadius: 999, background: "rgba(255,255,255,0.18)", overflow: "hidden", marginTop: 12 }}>
      <div style={{
        height: "100%",
        width: `${Math.max(0, 1 - progress) * 100}%`,
        background: `linear-gradient(90deg, ${RED}, #ff5252)`,
        transition: "width 200ms linear",
        borderRadius: 999,
      }} />
    </div>
  );
}

export default function Overlay() {
  const socketRef = useRef(null);
  const [connected, setConnected]       = useState(false);
  const [ventaState, setVentaState]     = useState(null);
  const [subastaState, setSubastaState] = useState(null);
  const [now,  setNow]  = useState(Date.now());
  const [mode, setMode] = useState("idle");

  const idleTimer         = useRef(null);
  const prevVentaActive   = useRef(false);
  const prevSubastaActive = useRef(false);

  // ── Fondo transparente ──────────────────────────────────────────
  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.overflow = "hidden";
  }, []);

  // ── Ticker ──────────────────────────────────────────────────────
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(t);
  }, []);

  // ── Socket ──────────────────────────────────────────────────────
  useEffect(() => {
    const url = getBackendUrl();
    // polling primero para máxima compatibilidad con TikTok Studio
    const s = io(url, {
      path: "/socket.io",
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
    });
    socketRef.current = s;

    const joinDisplay = () => {
      setConnected(true);
      s.emit("join_display");
    };

    s.on("connect",   joinDisplay);
    s.on("reconnect", joinDisplay);
    s.on("disconnect", () => setConnected(false));
    s.on("connect_error", () => setConnected(false));

    s.on("display:snapshot", (snap) => {
      if (snap?.venta   !== undefined) setVentaState(snap.venta);
      if (snap?.subasta !== undefined) setSubastaState(snap.subasta);
    });
    s.on("venta:state",   (st) => setVentaState(st));
    s.on("subasta:state", (st) => setSubastaState(st));

    return () => s.disconnect();
  }, []);

  // ── Lógica de modos ─────────────────────────────────────────────
  useEffect(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    const va = !!ventaState?.active;
    const sa = !!subastaState?.active;

    if (va) { setMode("venta");   prevVentaActive.current = true;   return; }
    if (sa) { setMode("subasta"); prevSubastaActive.current = true; return; }

    if (prevVentaActive.current && !va) {
      prevVentaActive.current = false;
      if ((ventaState?.winners || []).length > 0) {
        setMode("ventaSold");
        idleTimer.current = setTimeout(() => setMode("idle"), 9000);
        return;
      }
    }
    if (prevSubastaActive.current && !sa) {
      prevSubastaActive.current = false;
      if ((subastaState?.leaderboard || []).length > 0) {
        setMode("subastaEnded");
        idleTimer.current = setTimeout(() => setMode("idle"), 9000);
        return;
      }
    }
    setMode("idle");
  }, [ventaState, subastaState]);

  // ── Timers ──────────────────────────────────────────────────────
  const ventaRemaining = useMemo(() => {
    if (!ventaState?.active || !ventaState?.endsAt) return null;
    return Math.ceil(Math.max(0, ventaState.endsAt - now) / 1000);
  }, [ventaState, now]);

  const ventaProgress = useMemo(() => {
    if (!ventaState?.startedAt || !ventaState?.endsAt) return 0;
    const dur = ventaState.endsAt - ventaState.startedAt;
    return dur > 0 ? Math.min(1, (now - ventaState.startedAt) / dur) : 0;
  }, [ventaState, now]);

  const subastaRemaining = useMemo(() => {
    if (!subastaState?.active || !subastaState?.endsAt) return null;
    return Math.ceil(Math.max(0, subastaState.endsAt - now) / 1000);
  }, [subastaState, now]);

  const subastaProgress = useMemo(() => {
    if (!subastaState?.endsAt) return 0;
    const w = subastaState.windowMs || 30000;
    return Math.min(1, 1 - Math.max(0, subastaState.endsAt - now) / w);
  }, [subastaState, now]);

  const fmt  = (p) => p != null && Number.isFinite(Number(p)) ? `${Number(p).toFixed(2)}€` : "—";
  const isIdle = mode === "idle";

  const winner    = (ventaState?.winners || [])[0] || null;
  const subWinner = (subastaState?.leaderboard || [])[0] || null;
  const top       = subastaState?.top || null;

  // ── Estilos ─────────────────────────────────────────────────────
  const card = {
    background: "rgba(8,8,12,0.90)",
    border: "1.5px solid rgba(255,255,255,0.13)",
    borderRadius: 20,
    padding: "20px 24px",
    color: WHITE,
    boxShadow: "0 8px 48px rgba(0,0,0,0.75)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
    width: 640,
    boxSizing: "border-box",
  };

  const badgeStyle = (bg = RED) => ({
    display: "inline-flex", alignItems: "center", gap: 6,
    background: bg, color: WHITE,
    borderRadius: 6, padding: "3px 12px",
    fontSize: 13, fontWeight: 800, letterSpacing: "0.08em",
    textTransform: "uppercase", marginBottom: 12,
  });

  const Avatar = ({ src, size = 60 }) => src
    ? <img src={src} alt="" style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,0.25)", flexShrink: 0 }} />
    : <div style={{ width: size, height: size, borderRadius: "50%", background: "rgba(255,255,255,0.1)", flexShrink: 0 }} />;

  // ── Contenido ───────────────────────────────────────────────────
  let content = null;

  if (mode === "venta") {
    content = (
      <>
        <div style={badgeStyle()}>🔴 Venta en directo</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <div style={{ fontSize: 64, fontWeight: 900, lineHeight: 1, letterSpacing: "-1px" }}>
            {fmt(ventaState?.price)}
          </div>
          {ventaState?.qty > 1 && (
            <div style={{ fontSize: 18, opacity: 0.65 }}>
              {(ventaState.winners || []).length}/{ventaState.qty} uds.
            </div>
          )}
        </div>
        <div style={{ fontSize: 17, opacity: 0.8, marginTop: 6 }}>
          ✍️ Escribe <strong>"mio"</strong> en el chat para comprar
          {ventaRemaining != null && <span style={{ opacity: 0.55 }}> · ⏱ {ventaRemaining}s</span>}
        </div>
        <TimerBar progress={ventaProgress} />
      </>
    );
  } else if (mode === "ventaSold") {
    content = (
      <>
        <div style={badgeStyle("#16a34a")}>🎉 ¡Vendido!</div>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <Avatar src={winner?.profilePictureUrl} size={72} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 30, fontWeight: 900, lineHeight: 1.2, wordBreak: "break-word" }}>
              {winner?.nickname || winner?.uniqueId || "—"}
            </div>
            <div style={{ fontSize: 44, fontWeight: 900, color: RED, lineHeight: 1 }}>
              {fmt(ventaState?.price)}
            </div>
          </div>
        </div>
      </>
    );
  } else if (mode === "subasta") {
    content = (
      <>
        <div style={badgeStyle()}>🔴 Subasta en directo</div>
        {!top ? (
          <div style={{ fontSize: 22, opacity: 0.8 }}>Sin pujas aún — ¡comenta tu puja!</div>
        ) : (
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <Avatar src={top.profilePictureUrl} size={64} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, opacity: 0.6 }}>Puja más alta</div>
              <div style={{ fontSize: 52, fontWeight: 900, lineHeight: 1, letterSpacing: "-0.5px" }}>
                {fmt(top.amount)}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, opacity: 0.9, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {top.nickname || top.uniqueId}
              </div>
            </div>
          </div>
        )}
        {subastaState?.minBid > 0 && (
          <div style={{ fontSize: 13, opacity: 0.5, marginTop: 4 }}>Puja mínima: <strong>{fmt(subastaState.minBid)}</strong></div>
        )}
        <div style={{ fontSize: 14, opacity: 0.55, marginTop: 6 }}>
          ✍️ Comenta tu puja para participar · ⏱ {subastaRemaining ?? "—"}s
        </div>
        <TimerBar progress={subastaProgress} />
      </>
    );
  } else if (mode === "subastaEnded") {
    content = (
      <>
        <div style={badgeStyle("#16a34a")}>🏆 ¡Subasta resuelta!</div>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <Avatar src={subWinner?.profilePictureUrl} size={72} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 28, fontWeight: 900, lineHeight: 1.2, wordBreak: "break-word" }}>
              {subWinner?.nickname || subWinner?.uniqueId || "—"}
            </div>
            <div style={{ fontSize: 44, fontWeight: 900, color: RED, lineHeight: 1 }}>
              {fmt(subWinner?.amount)}
            </div>
          </div>
        </div>
        {(subastaState?.leaderboard || []).slice(1, 4).map((b, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 15, opacity: 0.6, marginTop: 6, paddingTop: i === 0 ? 8 : 0, borderTop: i === 0 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
            <span>{i + 2}. {b.nickname || b.uniqueId}</span>
            <span style={{ fontWeight: 700 }}>{fmt(b.amount)}</span>
          </div>
        ))}
      </>
    );
  }

  return (
    <div style={{ background: "transparent", display: "inline-block" }}>

      {/* Indicador de conexión — pequeño punto visible solo cuando está desconectado */}
      {!connected && (
        <div style={{
          position: "fixed", top: 8, left: 8,
          background: "rgba(0,0,0,0.6)", borderRadius: 6,
          padding: "3px 8px", fontSize: 11, color: "#f87171",
          fontFamily: "monospace",
        }}>
          ⚠ Sin conexión
        </div>
      )}

      {/* Card principal */}
      <div style={{
        transition: "opacity .35s ease, transform .35s cubic-bezier(.22,1,.36,1)",
        opacity:   isIdle ? 0 : 1,
        transform: isIdle ? "translateY(12px)" : "translateY(0)",
      }}>
        {!isIdle && <div style={card}>{content}</div>}
      </div>
    </div>
  );
}
